import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiUnlock, FiX, FiAward, FiClock, FiCopy } from 'react-icons/fi';
import {
  listExamUnlocks,
  issueUnlockCode,
  denyUnlock,
  approveAdvancement,
  listPendingAdvancements,
  getQuestionRevisions,
  approveQuestionRevision,
  rejectQuestionRevision,
  bulkApproveRevisions
} from '../services/api';

// Admin/proctor view. Server-gated to can_manage_payroll. Three workflows:
//   1. Pending requests → issue code (in person) or deny.
//   2. Codes issued, awaiting use → see what's outstanding, code visible if user lost it.
//   3. Pending advancement approvals → techs who passed; click to bump their level.

export default function TrainingAdmin() {
  const [unlocks, setUnlocks] = useState([]);
  const [pendingAdv, setPendingAdv] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [issuedCode, setIssuedCode] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([listExamUnlocks(), listPendingAdvancements()])
      .then(([u, p]) => {
        setUnlocks(u.data.unlocks || []);
        setPendingAdv(p.data.attempts || []);
        setError('');
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleIssue = async (unlock) => {
    if (!window.confirm(
      `Issue a one-time unlock code for ${unlock.member_name}'s exam on "${unlock.module_title}"?\n\n` +
      `Be physically present to proctor — the code expires in 2 hours.`
    )) return;
    setBusyId(unlock.id);
    try {
      const res = await issueUnlockCode(unlock.id);
      setIssuedCode({
        code: res.data.code,
        expires_at: res.data.expires_at,
        member_name: res.data.member_name,
        module_title: res.data.module_title
      });
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to issue code.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDeny = async (unlock) => {
    if (!window.confirm(`Deny ${unlock.member_name}'s exam request for "${unlock.module_title}"?`)) return;
    setBusyId(unlock.id);
    try {
      await denyUnlock(unlock.id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to deny.');
    } finally {
      setBusyId(null);
    }
  };

  const handleApprove = async (attempt) => {
    if (!window.confirm(
      `Approve advancement for ${attempt.member_name} based on their pass of "${attempt.module_title}" (${attempt.score}%)?\n\n` +
      `This will bump their career-track level.`
    )) return;
    setBusyId('adv-' + attempt.id);
    try {
      await approveAdvancement(attempt.id);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to approve advancement.');
    } finally {
      setBusyId(null);
    }
  };

  const copyCode = () => {
    if (issuedCode?.code) {
      navigator.clipboard.writeText(issuedCode.code).catch(() => {});
    }
  };

  const requested = unlocks.filter(u => u.status === 'requested');
  const unlocked = unlocks.filter(u => u.status === 'unlocked');
  const recent = unlocks.filter(u => ['used', 'denied', 'expired'].includes(u.status)).slice(0, 15);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1100, margin: '0 auto' }}>
      <Link
        to="/training"
        style={{ color: '#1e40af', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, textDecoration: 'none' }}
      >
        <FiArrowLeft size={14} /> Back to training
      </Link>

      <h1 style={{ margin: '12px 0 6px 0', color: '#0f172a' }}>Training Admin / Proctor</h1>
      <p style={{ margin: '0 0 24px 0', color: '#64748b', fontSize: 14 }}>
        Issue unlock codes for techs ready to take their level exam, and approve advancement after a pass.
        You should be physically present during the exam — that's the proctor moment.
      </p>

      {issuedCode && (
        <div
          style={{
            background: '#ecfeff',
            border: '2px solid #06b6d4',
            borderRadius: 12,
            padding: 20,
            marginBottom: 24
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 13, color: '#0e7490', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Unlock code for {issuedCode.member_name}
              </div>
              <div style={{ fontSize: 13, color: '#0e7490', marginTop: 2 }}>
                {issuedCode.module_title} · expires {new Date(issuedCode.expires_at + 'Z').toLocaleString()}
              </div>
            </div>
            <button
              onClick={() => setIssuedCode(null)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0e7490' }}
            >
              <FiX size={20} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
            <div
              style={{
                fontFamily: 'Consolas, Menlo, monospace',
                fontSize: 36,
                fontWeight: 700,
                letterSpacing: 8,
                color: '#0f172a',
                background: 'white',
                padding: '12px 20px',
                borderRadius: 8,
                border: '1px solid #cffafe'
              }}
            >
              {issuedCode.code}
            </div>
            <button
              onClick={copyCode}
              style={{
                display: 'inline-flex',
                gap: 6,
                alignItems: 'center',
                background: '#0891b2',
                color: 'white',
                border: 'none',
                padding: '10px 16px',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              <FiCopy /> Copy
            </button>
          </div>
          <p style={{ margin: '12px 0 0 0', fontSize: 13, color: '#0e7490' }}>
            Read or show the code to the tech on their device, then watch them take the exam.
          </p>
        </div>
      )}

      {loading && <p>Loading…</p>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8 }}>{error}</div>}

      <Section
        title="Pending advancement approvals"
        emptyText="No passed exams awaiting approval."
        count={pendingAdv.length}
        accent="#16a34a"
      >
        {pendingAdv.map(a => (
          <Row key={a.id}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{a.member_name}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                Passed {a.module_title} · score {a.score}% ({a.correct_count}/{a.total_count}) ·
                {a.completed_at && ' completed ' + new Date(a.completed_at + 'Z').toLocaleString()}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link
                to={`/training/exam/${a.id}`}
                style={{
                  display: 'inline-flex',
                  gap: 6,
                  alignItems: 'center',
                  background: '#1e293b',
                  color: 'white',
                  padding: '8px 14px',
                  borderRadius: 6,
                  textDecoration: 'none',
                  fontSize: 13,
                  fontWeight: 600
                }}
              >
                Review attempt
              </Link>
              <button
                onClick={() => handleApprove(a)}
                disabled={busyId === 'adv-' + a.id}
                style={btnStyle('#16a34a')}
              >
                <FiAward /> Approve advancement
              </button>
            </div>
          </Row>
        ))}
      </Section>

      <Section
        title="Pending exam requests"
        emptyText="No pending exam requests."
        count={requested.length}
        accent="#c2410c"
      >
        {requested.map(u => (
          <Row key={u.id}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{u.member_name}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                {u.module_title} · requested {new Date(u.requested_at + 'Z').toLocaleString()}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => handleIssue(u)}
                disabled={busyId === u.id}
                style={btnStyle('#16a34a')}
              >
                <FiUnlock /> Issue code
              </button>
              <button
                onClick={() => handleDeny(u)}
                disabled={busyId === u.id}
                style={btnStyle('#dc2626', true)}
              >
                <FiX /> Deny
              </button>
            </div>
          </Row>
        ))}
      </Section>

      <Section
        title="Codes issued, awaiting use"
        emptyText="No outstanding codes."
        count={unlocked.length}
        accent="#1e40af"
      >
        {unlocked.map(u => (
          <Row key={u.id}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{u.member_name}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{u.module_title}</div>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div
                style={{
                  fontFamily: 'Consolas, Menlo, monospace',
                  fontSize: 18,
                  letterSpacing: 4,
                  color: '#0f172a',
                  background: '#f1f5f9',
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontWeight: 700
                }}
              >
                {u.code}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                <FiClock style={{ verticalAlign: 'middle', marginRight: 4 }} />
                expires {u.expires_at && new Date(u.expires_at + 'Z').toLocaleTimeString()}
              </div>
            </div>
          </Row>
        ))}
      </Section>

      <Section
        title="Recent activity"
        emptyText=""
        count={recent.length}
        accent="#64748b"
      >
        {recent.map(u => (
          <Row key={u.id}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{u.member_name}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                {u.module_title} ·{' '}
                <span style={{ textTransform: 'capitalize' }}>{u.status}</span>
                {u.used_at && ' at ' + new Date(u.used_at + 'Z').toLocaleString()}
              </div>
            </div>
          </Row>
        ))}
      </Section>

      <RevisionsSection />
    </div>
  );
}

function RevisionsSection() {
  const [revs, setRevs] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await getQuestionRevisions('pending');
      setRevs(r.data.revisions || []);
      setPendingCount(r.data.pending_count || 0);
    } catch (e) { setRevs([]); }
  };
  useEffect(() => { load(); }, []);

  const onApprove = async (rev, edited) => {
    setBusy(rev.id);
    try { await approveQuestionRevision(rev.id, edited); await load(); }
    catch (e) { alert('Could not approve'); }
    setBusy(null);
  };
  const onReject = async (rev) => {
    setBusy(rev.id);
    try { await rejectQuestionRevision(rev.id); await load(); }
    catch (e) { alert('Could not reject'); }
    setBusy(null);
  };
  const onBulkApprove = async (ids, title) => {
    if (!window.confirm(`Approve all ${ids.length} revisions in "${title}"? This updates those live questions.`)) return;
    setBusy('bulk');
    try { await bulkApproveRevisions(ids); await load(); }
    catch (e) { alert('Bulk approve failed'); }
    setBusy(null);
  };

  if (revs === null) return null;

  // group by module
  const groups = {};
  revs.forEach(r => { const k = r.module_title || 'Unassigned'; (groups[k] = groups[k] || []).push(r); });

  return (
    <section style={{ marginTop: 32 }}>
      <h2 style={{ fontSize: 16, color: '#0f172a', margin: '0 0 4px' }}>
        Answer Revisions to Review {pendingCount > 0 && <span style={{ color: '#dc2626' }}>({pendingCount})</span>}
      </h2>
      <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: 13 }}>
        Proposed rewrites of the wrong answers so the correct one isn't always the longest. Spot-check a few, then approve the whole module — or approve/reject individually. Nothing changes on the live questions until you approve.
      </p>
      {revs.length === 0 ? (
        <div style={{ color: '#64748b', fontSize: 14, padding: '12px 0' }}>No revisions pending review.</div>
      ) : (
        Object.entries(groups).map(([title, list]) => (
          <div key={title} style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f1f5f9', padding: '8px 12px', borderRadius: 8, marginBottom: 10 }}>
              <strong style={{ color: '#0f172a' }}>{title} <span style={{ color: '#64748b', fontWeight: 400 }}>· {list.length} pending</span></strong>
              <button disabled={busy === 'bulk'} onClick={() => onBulkApprove(list.map(r => r.id), title)}
                style={{ padding: '6px 12px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer', fontSize: 13 }}>
                {busy === 'bulk' ? '…' : `Approve all ${list.length}`}
              </button>
            </div>
            {list.map(rev => <RevisionCard key={rev.id} rev={rev} busy={busy === rev.id} onApprove={onApprove} onReject={onReject} />)}
          </div>
        ))
      )}
    </section>
  );
}

function RevisionCard({ rev, busy, onApprove, onReject }) {
  const [form, setForm] = useState({
    a: rev.proposed?.a || '', b: rev.proposed?.b || '', c: rev.proposed?.c || '', d: rev.proposed?.d || '',
    correct: rev.proposed?.correct || 'A',
  });
  const letters = ['a', 'b', 'c', 'd'];
  const cell = { padding: '6px 8px', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 13 };
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 10 }}>{rev.prompt}</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#9ca3af', marginBottom: 6 }}>Current (live)</div>
          {letters.map(L => rev.original?.[L] != null && (
            <div key={L} style={{ ...cell, marginBottom: 4, background: rev.original.correct === L.toUpperCase() ? '#dcfce7' : '#f9fafb' }}>
              <strong>{L.toUpperCase()}.</strong> {rev.original[L]}{rev.original.correct === L.toUpperCase() ? '  ✓' : ''}
            </div>
          ))}
        </div>
        <div>
          <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#9ca3af', marginBottom: 6 }}>Proposed (editable)</div>
          {letters.map(L => (rev.proposed?.[L] != null && rev.proposed?.[L] !== '') && (
            <div key={L} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
              <input type="radio" name={`correct-${rev.id}`} checked={form.correct === L.toUpperCase()} onChange={() => setForm(f => ({ ...f, correct: L.toUpperCase() }))} title="Mark correct" />
              <input style={{ ...cell, flex: 1, background: form.correct === L.toUpperCase() ? '#dcfce7' : 'white' }} value={form[L]} onChange={(e) => setForm(f => ({ ...f, [L]: e.target.value }))} />
            </div>
          ))}
        </div>
      </div>
      {rev.rationale && <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>Why: {rev.rationale}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button disabled={busy} onClick={() => onApprove(rev, form)} style={{ padding: '7px 14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
          {busy ? '…' : 'Approve → update live'}
        </button>
        <button disabled={busy} onClick={() => onReject(rev)} style={{ padding: '7px 14px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
          Reject
        </button>
      </div>
    </div>
  );
}

function Section({ title, count, emptyText, children, accent = '#1e40af' }) {
  const isEmpty = React.Children.count(children) === 0;
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: 15, color: '#0f172a', margin: '0 0 10px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 6, height: 18, background: accent, borderRadius: 3 }} />
        {title}
        {count > 0 && (
          <span style={{
            background: accent,
            color: 'white',
            fontSize: 11,
            padding: '2px 8px',
            borderRadius: 10,
            fontWeight: 700
          }}>
            {count}
          </span>
        )}
      </h2>
      {isEmpty ? (
        emptyText ? <div style={{ fontSize: 13, color: '#64748b', paddingLeft: 14 }}>{emptyText}</div> : null
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {children}
        </div>
      )}
    </section>
  );
}

function Row({ children }) {
  return (
    <div
      style={{
        padding: 12,
        background: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap'
      }}
    >
      {children}
    </div>
  );
}

function btnStyle(color, outline = false) {
  return {
    display: 'inline-flex',
    gap: 6,
    alignItems: 'center',
    background: outline ? 'white' : color,
    color: outline ? color : 'white',
    border: outline ? `1px solid ${color}` : 'none',
    padding: '8px 14px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600
  };
}
